import React, { useState, useEffect } from 'react'
import { T, Spinner, Empty } from '../ui'
import { getNFLOptimizer, getNFLWaivers } from '../api'
import { teamNick } from '../plain'

// Lineup — set the optimal starting lineup from your team, and find waiver adds.
// Build "my team" by tapping ＋ Add to my team on any player's case (Rankings mode);
// this panel then optimizes the starters and surfaces available upgrades.

const SLOT_LABEL = { QB: 'QB', RB: 'RB', WR: 'WR', TE: 'TE', FLEX: 'FLEX' }
const ptsCol = p => p >= 18 ? T.green : p >= 12 ? T.mint : p >= 8 ? T.amber : T.muted2
const gradeCol = g => g === 'A' ? T.green : g === 'B' ? T.mint : g === 'C' ? T.amber : g === 'D' ? T.red : T.muted

function SlotRow({ s }) {
  const empty = s.empty
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '9px 12px',
      borderBottom: `1px solid ${T.border}` }}>
      <span style={{ width: 40, fontSize: 10.5, fontWeight: 800, color: T.muted, letterSpacing: '0.04em', flexShrink: 0 }}>{SLOT_LABEL[s.slot]}</span>
      {empty ? <span style={{ flex: 1, fontSize: 12.5, color: T.faint, fontStyle: 'italic' }}>— no eligible player —</span>
        : (<>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13.5, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.name}</div>
            <div style={{ fontSize: 10.5, color: T.muted }}>{s.pos} · {s.team} {s.pos === 'QB' ? '@' : 'vs'} {s.opp}
              {s.grade !== '—' && <span style={{ color: gradeCol(s.grade), marginLeft: 5 }}>· {s.grade}</span>}</div>
          </div>
          <span style={{ fontFamily: T.mono, fontWeight: 700, fontSize: 15, color: ptsCol(s.pts), flexShrink: 0 }}>{s.pts}</span>
        </>)}
    </div>
  )
}

export default function LineupPanel({ season, week, scoring, team, onAddTeam, onRemoveTeam, onClearTeam }) {
  const [opt, setOpt] = useState(null)
  const [wai, setWai] = useState(null)
  const ids = team.map(t => t.gsis_id)

  useEffect(() => {
    let alive = true; setOpt(null); setWai(null)
    getNFLOptimizer(ids, season, week, scoring).then(d => { if (alive) setOpt(d) })
    getNFLWaivers(season, week, scoring, ids).then(d => { if (alive) setWai(d.rows || []) })
    return () => { alive = false }
  }, [ids.join(','), season, week, scoring])

  const teamSet = new Set(ids)
  const hasTeam = team.length > 0

  return (
    <div>
      {/* My team status */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <span style={{ fontSize: 12.5, fontWeight: 800 }}>{hasTeam ? `My team · ${team.length}` : 'Optimal lineup'}</span>
        {hasTeam && <button onClick={onClearTeam} style={{ fontSize: 11, fontWeight: 700, color: T.muted, background: 'transparent', border: 'none', cursor: 'pointer' }}>clear team</button>}
      </div>

      {!hasTeam && (
        <div style={{ fontSize: 11, color: T.muted, background: T.card2, border: `1px solid ${T.border}`, borderRadius: 9, padding: '9px 12px', marginBottom: 10, lineHeight: 1.5 }}>
          Showing the <b style={{ color: T.text }}>slate's best possible lineup</b>. Tap a player in <b style={{ color: T.green }}>Rankings</b> → <b style={{ color: T.text }}>＋ Add to my team</b> to build your roster and optimize your starters.
        </div>
      )}

      {/* Optimal lineup */}
      {!opt ? <Spinner /> : (
        <div style={{ background: T.card, border: `1px solid ${T.border2}`, borderRadius: 13, overflow: 'hidden', marginBottom: 8 }}>
          {opt.lineup.map((s, i) => <SlotRow key={i} s={s} />)}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: T.greenDim }}>
            <span style={{ fontSize: 11.5, fontWeight: 800, color: T.green, letterSpacing: '0.04em' }}>PROJECTED TOTAL</span>
            <span style={{ fontFamily: T.mono, fontWeight: 900, fontSize: 18, color: T.green }}>{opt.total}</span>
          </div>
        </div>
      )}

      {/* Bench (my-team mode only) */}
      {hasTeam && opt && opt.bench.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 10.5, color: T.muted, margin: '10px 2px 6px', letterSpacing: '0.05em' }}>BENCH · SIT</div>
          {opt.bench.map(b => (
            <div key={b.gsis_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '7px 12px', background: T.card, border: `1px solid ${T.border}`, borderRadius: 9, marginBottom: 5 }}>
              <span style={{ fontSize: 12.5 }}>{b.name} <span style={{ color: T.muted, fontSize: 11 }}>{b.pos} · {b.team}</span></span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontFamily: T.mono, fontSize: 12.5, color: ptsCol(b.pts) }}>{b.pts}</span>
                <span onClick={() => onRemoveTeam(b)} style={{ fontSize: 12, color: T.muted, cursor: 'pointer', fontWeight: 800 }}>✕</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Waiver targets */}
      <div style={{ fontSize: 12.5, fontWeight: 800, margin: '16px 2px 4px' }}>Waiver targets <span style={{ color: T.muted, fontWeight: 600, fontSize: 11 }}>· likely available</span></div>
      <div style={{ fontSize: 10.5, color: T.muted, marginBottom: 9 }}>Startable-range players outside the start tier — rising roles & soft matchups first.</div>
      {!wai ? <Spinner />
        : !wai.length ? <Empty message="No clear waiver adds this week." />
        : wai.map(r => {
          const added = teamSet.has(r.gsis_id)
          return (
            <div key={r.gsis_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
              background: T.card, border: `1px solid ${r.trend >= 12 ? T.greenMid : T.border}`, borderRadius: 11, marginBottom: 7 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 700 }}>{r.name} <span style={{ color: T.muted, fontWeight: 400, fontSize: 11 }}>{r.pos} · {teamNick(r.team)} vs {teamNick(r.opp)}</span></div>
                <div style={{ fontSize: 11, color: r.trend >= 12 ? T.green : T.muted, marginTop: 2 }}>
                  {r.trend >= 12 ? '▲ ' : ''}{r.reason}
                </div>
              </div>
              <span style={{ fontFamily: T.mono, fontWeight: 700, fontSize: 15, color: ptsCol(r.pts), flexShrink: 0 }}>{r.pts}</span>
              <button onClick={() => added ? onRemoveTeam(r) : onAddTeam(r)} title={added ? 'On my team' : 'Add to my team'}
                style={{ width: 30, height: 30, borderRadius: 8, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 15, fontWeight: 900, cursor: 'pointer', background: added ? T.green : T.greenDim,
                  color: added ? '#04140D' : T.green, border: `1px solid ${T.green}` }}>{added ? '✓' : '+'}</button>
            </div>
          )
        })}
    </div>
  )
}
